<template>
  <div>
    <div class="subscription-overview-header">
      <div class="toggle-wrapper">
        <h3 class="subscription-overview-text">
          {{ title }}
        </h3>

        <RexRadioV3
          v-if="isToggleEnabled"
          class="tags-condition"
          optionType="button"
          v-model="currentView"
          :options="radioGroupOptions"
        ></RexRadioV3>
      </div>
      <div
        v-if="currentView === constants.tableView"
        class="subscription-overview-search"
      >
        <RexSearch
          :placeholder="placeholder"
          @search="
            (searchTerm) => filterTable(searchTerm.text, tableFilterField)
          "
          @clearSelection="handleClear"
          @input="updateSearchTerm"
          width="15rem"
          class="search"
        />

        <RexPdfGenerator
          @pdfContentGenerate="generatePdfContent"
          ref="pdfGenerator"
          :downloadLabel="$t('aws.downloadReport')"
          :downloadVariant="getPdfDownloadButtonVariant"
          :fileName="getFileNameForPdf"
          :title="getFileNameForPdf"
          :reportPath="getReportPath"
        />
      </div>
    </div>

    <!-- Conditionally Render Table or Placeholder Text -->
    <div v-if="currentView === constants.tableView">
      <RexTableCompV2
        :headers="headers"
        :items="filteredData"
        :noDataMessage="
          title.includes('Subscription') && $t('aws.noSubscriptionsSelected')
        "
        variant="primary"
        height="auto"
        :enableDetails="true"
        :striped="true"
      >
        <!-- Slot for headers -->
        <template
          v-for="(header, index) in headers"
          #[`header-${header.key}`]
          :key="index"
        >
          <div :class="index === 0 ? 'table-header-first' : 'table-header'">
            {{ header.name }}
            <span v-if="showTooltip">
              <BaseIcon
                v-if="header.tooltip"
                class="tooltipIcon"
                name="infoOutlinev2sm"
                v-tooltip:top="{
                  content: header.tooltip,
                  disabled: false,
                }"
              />
            </span>
          </div>
        </template>

        <!-- Slot for body -->
        <template
          v-for="(header, index) in headers"
          :key="index"
          #[`body-${header.key}`]="{ item }"
        >
          <div :key="index" :class="index === 0 ? '' : 'table-content'">
            <!-- Apply click handler only to the first column -->
            <template v-if="index === 0 && !(header.key in logsData)">
              <div @click="handleClick(item[header.key])" class="first-column">
                {{ item[header.key] }}
              </div>
            </template>
            <template v-else>
              <div v-if="header.key in showDataInGb">
                {{ getDataInGB(item[header.key], 2, false) }}
              </div>
              <div v-else-if="header.convertTo">
                {{ convertBytes(item[header.key], header.convertTo) }}
              </div>
              <div v-else>
                {{ item[header.key] ?? '-' }}
              </div>
            </template>
          </div>
        </template>
      </RexTableCompV2>

      <div class="pagination">
        <RexPaginationV3
          v-if="enablePagination"
          @stateChange="handlePageChange"
          :defaultPageNo="currentPage"
          :total="getTotalRecords"
          :defaultPageSize="pageSize"
        />
      </div>
    </div>

    <div v-else>
      <div class="first-set">
        <div
          class="chart-element"
          v-for="(chartConfig, index) in getModifiedTemplate"
          :key="index"
          :style="{ width: chartConfig.width }"
        >
          <GraphBuilder
            :chartConfig="chartConfig"
            :index="index"
            :layoutIndex="0"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RexTableCompV2 from '@/rex-components/rex-TableCompV2.vue';
import cloudTemplate from '@/utils/templates/cloud.template';
import GraphBuilder from '@/components/Common/GraphBuilder.vue';
import RexPdfGenerator from '@/rex-components/rex-pdf-generator.vue';
import { pdfConfig } from '@/utils/pdfUtils';
import {
  getDataInGB,
  convertDateFormat,
  convertBytes,
} from '@/utils/helper-function';
import RexSearch from '@/rex-components/rex-search.vue';
import { getColor } from '@/components/charts/util';
import RexRadioV3 from '@/rex-components/RexRadioV3.vue';
import {
  fileNameMapper,
  overviewTabsradioGroupOptions,
  showDataInGb,
} from '@/components/Cloud/Dashboard/cloud.types';
import { constants } from '@/components/Cloud/Dashboard/pages/lambda/lambda.types';
import RexPaginationV3 from '@/rex-components/RexPaginationV3.vue';
import { mapGetters } from 'vuex';
import lambdaCharts from '@/utils/templates/cloudTemplates/aws/lambda.config';
import ingressCharts from '@/utils/templates/cloudTemplates/aws/ingress.config.js';
import { serviceNames } from '@/components/Cloud/services/aws/aws.services.types';
import elastiCacheCharts from '@/utils/templates/cloudTemplates/aws/elasticache.config';
import rdsMySqlCharts from '@/utils/templates/cloudTemplates/aws/rds.mysql.config';

export default {
  name: 'AWSTable',
  components: {
    RexTableCompV2,
    RexPdfGenerator,
    RexSearch,
    GraphBuilder,
    RexRadioV3,
    RexPaginationV3,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      required: true,
    },
    headers: {
      type: Array,
      required: true,
    },
    data: {
      type: Array,
      required: true,
    },
    isToggleEnabled: {
      type: Boolean,
      default: false,
    },
    tableFilterField: {
      type: String,
      default: 'subscriptionId',
    },
    pdfTitle: {
      type: String,
      default: '',
    },
    paginationFunction: {
      type: Function,
      default: () => {},
    },
    enablePagination: {
      type: Boolean,
      default: false,
    },
    totalRecords: {
      type: Number,
      default: 10,
    },
    currentSubType: {
      type: String,
      default: '',
    },
  },
  emits: ['subscriptionClicked'],
  data() {
    return {
      styling: {
        width: '150px',
      },
      searchTerm: '', // State to hold search term
      filteredData: [], // Array to hold filtered data
      currentView: 'tableView',
      toggleData: [this.$t('k8.tableView'), this.$t('k8.mapView')],
      awsTemplates: cloudTemplate,
      subscriptionIds: [],
      constants,
      fileNameMapper,
      pageSize: 10,
      pageNum: 1,
      currentPage: 1,
    };
  },
  computed: {
    ...mapGetters('globalState/settings', ['getDurationTime']),
    showDataInGb,
    logsData() {
      return {
        timestamp: 'timestamp',
      };
    },
    showTooltip() {
      return this.title === this.$t('aws.invocations');
    },
    getTotalRecords() {
      if (this.searchTerm) {
        return this.filteredData.length;
      }
      return this.totalRecords;
    },
    getFileNameForPdf() {
      if (this.pdfTitle) {
        return this.pdfTitle;
      }
      return fileNameMapper[this.tableFilterField];
    },
    isSubscription() {
      return this.placeholder.includes('subscription');
    },
    getStatesForSearch() {
      return this.data.map((item) => ({
        key: item.subscriptionId,
        label: item.subscriptionId,
      }));
    },
    getPdfDownloadButtonVariant() {
      return this.data.length > 0 ? 'primary' : 'disabled';
    },
    getTemplate() {
      const chartMappers = {
        [serviceNames.RDS]: rdsMySqlCharts(),
        [serviceNames.LAMBDA]: lambdaCharts(),
        [serviceNames.ELB]: ingressCharts(),
        [serviceNames.ELASTICACHE]: elastiCacheCharts(),
      };
      return chartMappers[this.currentSubType]?.lineChartsOverviewPage;
    },
    getModifiedTemplate() {
      // Modify each chartConfig to include subscription IDs in metricName and a color field
      const modifiedTemplate = this.getTemplate.map((chartConfig) => ({
        ...chartConfig,
      }));
      return modifiedTemplate;
    },
    radioGroupOptions() {
      return overviewTabsradioGroupOptions();
    },
  },
  methods: {
    getDataInGB,
    convertDateFormat,
    convertBytes,
    handleClick(subscriptionId) {
      this.$emit('subscriptionClicked', subscriptionId);
    },
    filterTable(searchTerm) {
      const lowerCaseSearchTerm = searchTerm.trim().toLowerCase();
      if (!lowerCaseSearchTerm) {
        this.filteredData = this.data;
        return;
      }
      this.filteredData = this.data.filter((item) => {
        return item[this.tableFilterField]
          .toLowerCase()
          .includes(lowerCaseSearchTerm);
      });
    },
    handleClear() {
      // Handle clear event
      this.searchTerm = '';
    },
    updateSearchTerm(newTerm) {
      // Sync the searchTerm with the input from the component
      this.searchTerm = newTerm;
    },
    transformData(headers, data) {
      // Extract header names from the first array
      const headerNames = headers.map((item) => item.name);

      // Initialize an array to hold the transformed data
      const transformedData = {
        header: headerNames,
        body: [],
      };

      // Iterate over each object in the data array
      data.forEach((item) => {
        // Create an array to hold values in the same order as headers
        const row = headers.map((header) => {
          const key = header.key;
          let value = item[key];
          // Apply GB conversion for specific fields
          if (
            key === 'service_rds_free_storage_space' ||
            key === 'rds_free_storage_space'
          ) {
            value = this.getDataInGB(value);
          } else if (header.convertTo) {
            value = this.convertBytes(value, header.convertTo);
          }

          return value;
        });

        // Push the row to the body of transformedData
        transformedData.body.push(row);
      });

      return transformedData;
    },
    generatePdfContent(doc) {
      const { header, body } = this.transformData(
        this.headers,
        this.filteredData,
      );
      doc.autoTable({
        ...pdfConfig,
        head: [header],
        body,
      });
    },
    handleToggleChange(isChecked) {
      this.currentView = !isChecked;
    },
    handlePageChange(currState) {
      this.currentPage = currState.pageNo;
      this.pageSize = currState.pageSize;
      this.paginationFunction(this.currentPage, this.pageSize);
    },
  },
  mounted() {
    if (this.enablePagination) {
      this.paginationFunction(this.currentPage, this.pageSize);
    }
  },
  watch: {
    searchTerm(currentValue) {
      if (!currentValue) {
        this.filterTable(currentValue);
      }
    },
    data: {
      handler: function (currentValue) {
        this.filteredData = currentValue;
      },
      immediate: true,
    },
    getDurationTime() {
      if (this.enablePagination) {
        this.paginationFunction(this.currentPage, this.pageSize);
      }
    },
    currentView() {
      this.filteredData = this.data;
    },
  },
};
</script>

<style lang="scss" scoped>
.subscription-overview-header {
  @include flex-center-justify(space-between);
  margin-bottom: 1.25rem;
}

.subscription-overview-text {
  color: white;
}

.subscription-overview-search {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.first-column {
  cursor: pointer;
  color: $color-brand-accent;
  text-decoration: underline;
}

.search-container {
  position: relative;
  display: flex;
}

.search {
  color: white;
  font-size: 1rem;
}

.filter-button {
  background-color: $color-brand-accent;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  cursor: pointer;
  border-radius: 0.25rem;
  font-size: 0.875rem;
}

.filter-button:hover {
  background-color: darken($color-brand-accent, 10%);
}

.subscription-list {
  position: absolute;
  background-color: white;
  border: 0.0625rem solid #ccc;
  list-style-type: none;
  padding: 0;
  margin: 0;
  border-radius: 0.25rem;
  max-height: 9.375rem;
  overflow-y: auto;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
}

.subscription-item {
  color: black;
  padding: 0.5rem;
  cursor: pointer;
}

.subscription-item:hover {
  background-color: #f0f0f0;
}

.search-container-input {
  width: 15rem;
}

.no-data {
  color: $color-brand-accent;
  padding: 3rem;
  font-size: 2rem;
}

.table-content {
  @include flex-center-horiz;
  width: 100%;
  overflow: hidden;
  overflow-wrap: break-word;
  word-break: break-all;
}

.table-header {
  text-align: center;
  font-size: 11px;
}

.table-header-first {
  font-size: 11px;
}

.toggle-wrapper {
  display: flex;
  align-items: center;
  gap: 15px;
}

.first-set {
  @include flex-center-justify(start);
  flex-wrap: wrap;
  gap: 1.25rem;
}

.tooltipIcon {
  margin-left: 5px;
}

.pagination {
  @include flex-center-justify(end);
  padding-top: 0.625rem;
}
</style>
